import { IconButton, RotateCcwIcon } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { FilterResultChip } from "./FilterResultChip";
import styles from "./FilterBar.module.css";

// 행 2 에 표시하는 적용 조건 한 개다. 문구와 해제는 호출부가 소유한다. 셸은 축 종류를 모른다.
export type FilterBarChip = {
  key: string;
  label: string;
  removeLabel: string;
  onRemove: () => void;
};

/**
 * 배치된 자리의 폭 예산이다. 구조는 같고 치수만 다르다(rules.md §17).
 *
 * - `page`(기본): 페이지 폭. 검색은 기본 20rem 이고 16~32rem 사이에서 늘거나 준다.
 * - `narrow`: 목록 사이드바 같은 좁은 레인(≤280px). 검색이 한 줄 전체를 쓰고, 축은
 *   `FilterSheetButton` 팝오버 안에 넣는다. 페이지 하한(16rem = 256px)이 이 폭을 넘기 때문이다.
 */
export type FilterBarLane = "page" | "narrow";

export type FilterBarProps = {
  // 행 1 맨 앞의 검색 컨트롤이다. 셸이 폭 규약(20rem basis, 16~32rem)을 적용한다.
  search?: ReactNode;
  // 행 1 의 나머지 컨트롤(facet 드롭다운·스위치)이다. size 를 지정하지 않고(md) 넣는다.
  children?: ReactNode;
  chips: readonly FilterBarChip[];
  // [↺ 초기화] 다. 칩이 하나라도 있을 때만 표시하고, 모든 축을 비운다.
  onClearAll: () => void;
  lane?: FilterBarLane;
  ariaLabel?: string;
  className?: string;
};

/**
 * 목록 필터 바 셸이다(rules.md §17). 행 1 은 `[검색][축 컨트롤…]`, 행 2 는 `[적용 조건 칩…][↺ 초기화]` 다.
 *
 * 축의 종류와 상태는 가지지 않는다. 페이지가 URL 에서 읽은 상태로 컨트롤과 칩을 만들어 전달한다.
 * 셸은 위치·폭·간격·칩 종류(`FilterResultChip`)·초기화 표시 조건만 고정한다. 결과 수는 여기
 * 없다. 목록 상단 `BulkActionBar` 띠가 소유한다. 페이지 헤더 `belowTabs` 슬롯에 두고, 본문 상단
 * 여백은 `bodyFlushTop` 으로 끈다. Datasets 와 같은 구조다.
 */
export function FilterBar({
  search,
  children,
  chips,
  onClearAll,
  lane = "page",
  ariaLabel = "목록 필터",
  className,
}: FilterBarProps): ReactNode {
  const searchClassName = lane === "narrow" ? styles.searchNarrow : styles.search;
  const chipsClassName = lane === "narrow" ? `${styles.chips} ${styles.chipsNarrow}` : styles.chips;
  return (
    <div
      className={className ? `${styles.block} ${className}` : styles.block}
      role="group"
      aria-label={ariaLabel}
    >
      <div className={styles.row}>
        {search != null ? <div className={searchClassName}>{search}</div> : null}
        {children}
      </div>
      {chips.length > 0 ? (
        <div className={chipsClassName} role="group" aria-label="적용된 필터">
          {chips.map((chip) => (
            <FilterResultChip
              key={chip.key}
              label={chip.label}
              removeLabel={chip.removeLabel}
              onRemove={chip.onRemove}
            />
          ))}
          <IconButton
            text="초기화"
            type="button"
            size="sm"
            variant="ghost"
            className={styles.clearAll}
            onClick={onClearAll}
          >
            <RotateCcwIcon />
          </IconButton>
        </div>
      ) : null}
    </div>
  );
}
